import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Lightbox from '../components/Lightbox';

const HomeContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 50%, #e2e8f0 100%);
  position: relative;
  overflow-x: hidden;
`;

const BackgroundEffects = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 80%, rgba(66, 153, 225, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(99, 179, 237, 0.06) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
`;

const Section = styled.section`
  position: relative;
  z-index: 1;
  padding: 4rem 2rem;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const HeroSection = styled(Section)`
  min-height: 90vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  padding-top: 8rem;

  @media (max-width: 768px) {
    min-height: 70vh;
    padding-top: 6rem;
  }
`;

const MainTitle = styled.h1`
  font-size: clamp(3rem, 8vw, 5rem);
  font-weight: 200;
  color: #1a365d;
  margin-bottom: 1rem;
  letter-spacing: 3px;
  line-height: 1.1;

  .highlight {
    background: linear-gradient(135deg, #3182ce, #4299e1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: 0 0 30px rgba(66, 153, 225, 0.3);
  }
`;

const Subtitle = styled.h2`
  font-size: clamp(1.2rem, 4vw, 1.8rem);
  color: #4a5568;
  margin-bottom: 2rem;
  font-weight: 300;
  letter-spacing: 2px;
`;

const Description = styled.p`
  font-size: clamp(1rem, 2vw, 1.2rem);
  color: #718096;
  line-height: 1.8;
  max-width: 700px;
  margin: 0 auto 3rem auto;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const PrimaryButton = styled(Link)`
  background: linear-gradient(135deg, #3182ce, #4299e1);
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(66, 153, 225, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(66, 153, 225, 0.4);
  }

  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1rem;
    width: 250px;
    text-align: center;
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: #3182ce;
  padding: 1rem 2.5rem;
  border: 2px solid #3182ce;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    background: #3182ce;
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(66, 153, 225, 0.3);
  }

  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1rem;
    width: 250px;
    text-align: center;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
  background: rgba(255, 255, 255, 0.8);
  padding: 2rem 1rem;
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(66, 153, 225, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(66, 153, 225, 0.15);
  }

  .number {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 200;
    color: #3182ce;
    display: block;
    margin-bottom: 0.5rem;
  }

  .label {
    font-size: clamp(0.8rem, 2vw, 0.9rem);
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #4a5568;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 0.5rem;
  }
`;

const YachtSection = styled(Section)`
  background: rgba(255, 255, 255, 0.7);
  border-radius: 30px;
  margin: 4rem auto;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(66, 153, 225, 0.2);
  box-shadow: 0 20px 40px rgba(66, 153, 225, 0.1);

  @media (max-width: 768px) {
    margin: 2rem auto;
    border-radius: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 2.8rem);
  color: #1a365d;
  text-align: center;
  margin-bottom: 3rem;
  font-weight: 300;
  letter-spacing: 2px;

  .highlight {
    color: #3182ce;
  }
`;

const YachtGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const YachtGallery = styled.div`
  display: grid;
  gap: 1rem;
`;

const MainImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(66, 153, 225, 0.15);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 15px 35px rgba(66, 153, 225, 0.2);
  }

  @media (max-width: 768px) {
    height: 250px;
  }
`;

const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(66, 153, 225, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(66, 153, 225, 0.15);
  }

  @media (max-width: 768px) {
    height: 100px;
  }
`;

const VideoContainer = styled.div`
  margin-top: 1rem;
  background: rgba(66, 153, 225, 0.05);
  padding: 1.5rem;
  border-radius: 15px;
  border: 1px solid rgba(66, 153, 225, 0.1);
  text-align: center;

  h4 {
    color: #3182ce;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    font-weight: 500;
  }

  video {
    width: 100%;
    max-width: 400px;
    border-radius: 10px;
    box-shadow: 0 4px 15px rgba(66, 153, 225, 0.1);
  }
`;

const SpecsContainer = styled.div`
  h3 {
    font-size: clamp(1.3rem, 3vw, 1.6rem);
    color: #1a365d;
    margin-bottom: 2rem;
    font-weight: 300;
  }
`;

const SpecsGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const SpecItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(66, 153, 225, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(66, 153, 225, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(66, 153, 225, 0.08);
    transform: translateX(5px);
  }

  .label {
    color: #3182ce;
    font-weight: 500;
    font-size: 0.9rem;
  }

  .value {
    color: #4a5568;
    font-size: 0.9rem;
    text-align: right;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    
    .value {
      text-align: left;
    }
  }
`;

const FeaturesContainer = styled.div`
  h4 {
    font-size: 1.2rem;
    color: #3182ce;
    margin-bottom: 1rem;
    font-weight: 500;
  }
`;

const FeaturesList = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  font-size: 0.9rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(66, 153, 225, 0.05);
  }

  .check {
    color: #22c55e;
    font-weight: bold;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .text {
    color: #4a5568;
    line-height: 1.5;
  }
`;

const JourneySection = styled(Section)`
  text-align: center;
`;

const JourneyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const JourneyCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(66, 153, 225, 0.05));
  padding: 2.5rem 2rem;
  border-radius: 20px;
  border: 1px solid rgba(66, 153, 225, 0.2);
  box-shadow: 0 8px 25px rgba(66, 153, 225, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, #3182ce, transparent);
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(66, 153, 225, 0.15);
  }

  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  h3 {
    color: #3182ce;
    margin-bottom: 1rem;
    font-size: 1.3rem;
    font-weight: 500;
  }

  p {
    color: #4a5568;
    line-height: 1.6;
    font-size: 0.95rem;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState({ src: '', alt: '' });

  const openLightbox = (src: string, alt: string) => {
    setLightboxImage({ src, alt });
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const specifications = [
    { label: 'Model', value: 'Amel Arctic 55' },
    { label: 'Year Built', value: '2014' },
    { label: 'Length Overall', value: '17.3 m (56.8 ft)' },
    { label: 'Hull Length', value: '16.72 m (54.9 ft)' },
    { label: 'Beam', value: '4.99 m (16.4 ft)' },
    { label: 'Draft', value: '2.2 m (7.2 ft)' },
    { label: 'Engine', value: 'Volvo Penta 110 HP (84 kW)' },
    { label: 'Fuel Capacity', value: '2000 L (528 gal)' },
    { label: 'Water Capacity', value: '800 L (211 gal)' },
    { label: 'Hull Material', value: 'Fiberglass with Kevlar reinforcement' }
  ];

  const features = [
    'Designed for safe navigation in Arctic and high latitudes',
    'Kevlar-reinforced hull for iceberg protection',
    'Enhanced heating system for interior comfort',
    'Extended water and fuel capacity for long passages',
    'Watertight bulkheads and sealed engine compartment',
    'Spacious central cockpit with hard top',
    'Automated sail handling systems',
    'Stylish interior with high-quality finishes',
    'Fully equipped for independent sailing'
  ];

  const journeyHighlights = [
    {
      icon: '🌊',
      title: 'ARC Rally',
      description: 'Start with the legendary Atlantic Rally for Cruisers from Gran Canaria to the Caribbean.'
    },
    {
      icon: '🏔️',
      title: 'Northwest Passage',
      description: 'Navigate the challenging Arctic route through pristine wilderness and ice.'
    },
    {
      icon: '🌍',
      title: 'World Circumnavigation',
      description: 'Complete the ultimate sailing adventure across all major oceans.'
    },
    {
      icon: '📱',
      title: 'Live Tracking',
      description: 'Follow our progress in real-time with GPS tracking and daily updates.'
    }
  ];

  return (
    <HomeContainer>
      <BackgroundEffects />
      
      <HeroSection>
        <MainTitle>
          <span className="highlight">Arctic</span> Owl
        </MainTitle>
        <Subtitle>{t('home.subtitle')}</Subtitle>
        <Description>
          {t('home.description')}
        </Description>
        
        <ButtonContainer>
          <PrimaryButton to="/tracking">{t('home.track_live_journey')}</PrimaryButton>
          {/* <SecondaryButton to="/tourists">{t('home.join_adventure')}</SecondaryButton> */}
        </ButtonContainer>
        
        <StatsContainer>
          <StatItem>
            <span className="number">13,200</span>
            <span className="label">{t('home.nautical_miles')}</span>
          </StatItem>
          <StatItem>
            <span className="number">4</span>
            <span className="label">{t('home.continents')}</span>
          </StatItem>
          <StatItem>
            <span className="number">12</span>
            <span className="label">{t('home.months')}</span>
          </StatItem>
        </StatsContainer>
      </HeroSection>

      <YachtSection>
        <SectionTitle>
          {t('home.meet_arctic_owl')}
        </SectionTitle>
        
        <YachtGrid>
          <YachtGallery>
            <MainImage 
              src="/images/helicopter2.jpg" 
              alt="Arctic Owl exterior view"
              onClick={() => openLightbox("/images/helicopter2.jpg", "Arctic Owl exterior view")}
            />
            
            <ThumbnailGrid>
              <Thumbnail 
                src="/images/interior1.jpg" 
                alt="Arctic Owl interior"
                onClick={() => openLightbox("/images/interior1.jpg", "Arctic Owl interior")}
              />
              <Thumbnail 
                src="/images/interior2.jpg" 
                alt="Arctic Owl salon"
                onClick={() => openLightbox("/images/interior2.jpg", "Arctic Owl salon")}
              />
            </ThumbnailGrid>
            
            <VideoContainer>
              <h4>🎬 {t('home.yacht_video_tour')}</h4>
              <video controls>
                <source src="/images/IMG_7728.MOV" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </VideoContainer>
          </YachtGallery>

          <SpecsContainer>
            <h3>{t('home.technical_specifications')}</h3>
            <SpecsGrid>
              {specifications.map((spec, index) => (
                <SpecItem key={index}>
                  <span className="label">{spec.label}:</span>
                  <span className="value">{spec.value}</span>
                </SpecItem>
              ))}
            </SpecsGrid>

            <FeaturesContainer>
              <h4>{t('home.arctic_ready_features')}</h4>
              <FeaturesList>
                {features.map((feature, index) => (
                  <FeatureItem key={index}>
                    <span className="check">✓</span>
                    <span className="text">{feature}</span>
                  </FeatureItem>
                ))}
              </FeaturesList>
            </FeaturesContainer>
          </SpecsContainer>
        </YachtGrid>
      </YachtSection>

      <JourneySection>
        <SectionTitle>
          {t('home.epic_journey_ahead')}
        </SectionTitle>
        
        <JourneyGrid>
          {journeyHighlights.map((item, index) => (
            <JourneyCard key={index}>
              <div className="icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </JourneyCard>
          ))}
        </JourneyGrid>
      </JourneySection>

      <Lightbox
        isOpen={lightboxOpen}
        imageSrc={lightboxImage.src}
        imageAlt={lightboxImage.alt}
        onClose={closeLightbox}
      />
    </HomeContainer>
  );
};

export default Home; 
